import useToggleModal from "hooks/useToggleModal";
import React, {
  createContext,
  ReactNode,
  useContext,
  useState,
  useCallback,
} from "react";
import PatientCreatorContainer from "components/patient-editor/PatientCreatorContainer";
import { AmplitudePageName } from "utils/amplitude";

type Context = {
  showPatientCreator: boolean;
  openPatientCreator: (props: {
    onApply: (props) => void;
    amplitudePageName: AmplitudePageName;
  }) => void;
  closePatientCreator: () => void;
};
const PatientCreatorContext = createContext<Context>({
  showPatientCreator: false,
  openPatientCreator: () => null,
  closePatientCreator: () => null,
});

type Props = { children: ReactNode };

const INITIAL_MODAL_PROPS = {
  onApply: () => null,
};

export const PatientCreatorContextProvider = ({ children }: Props) => {
  const [modalProps, setModalProps] = useState(INITIAL_MODAL_PROPS);
  const {
    open: openPatientCreator,
    close: closePatientCreator,
    show: showPatientCreator,
  } = useToggleModal(false);
  const [amplitudePageName, setAmplitudePageName] =
    useState<AmplitudePageName | null>(null);

  const handleOpenPatientCreator = useCallback(
    (props) => {
      setModalProps(props);
      setAmplitudePageName(props.amplitudePageName);
      openPatientCreator();
    },
    [openPatientCreator],
  );

  const handleClosePatientCreator = useCallback(() => {
    closePatientCreator();
    setModalProps(INITIAL_MODAL_PROPS);
    setAmplitudePageName(null);
  }, [closePatientCreator]);

  return (
    <>
      <PatientCreatorContext.Provider
        value={{
          showPatientCreator,
          openPatientCreator: handleOpenPatientCreator,
          closePatientCreator: handleClosePatientCreator,
        }}
      >
        {children}
      </PatientCreatorContext.Provider>
      <PatientCreatorContainer
        nextAction={modalProps.onApply}
        show={showPatientCreator}
        onHide={closePatientCreator}
        amplitudePageName={amplitudePageName!}
      />
    </>
  );
};

export const usePatientCreator = () => useContext(PatientCreatorContext);
